import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api.js';
import type { Distributor } from '../../types';
import { extractApiError } from '../../utils/pdfDownload';
import { formatReassignToast } from '../../utils/documentToast';
import {
  defaultOrderDistributorId,
  sortDistributorsForOrderCapture,
} from '../../utils/orderDistributorSort';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface DocumentReassignControlProps {
  kind: 'lieferschein' | 'bestellung';
  docId: string;
  docNumber: string;
  status: string;
  currentDistributorId?: string;
  onMoved: (documents: { kind: 'lieferschein' | 'bestellung'; id: string; number: string }[]) => void;
}

export const DocumentReassignControl: React.FC<DocumentReassignControlProps> = ({
  kind,
  docId,
  docNumber,
  status,
  currentDistributorId,
  onMoved,
}) => {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [currentId, setCurrentId] = useState('');
  const [nextId, setNextId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api
      .get('/distributors')
      .then((res) => {
        const sorted = sortDistributorsForOrderCapture(res.data as Distributor[]);
        setDistributors(sorted);
        const current =
          currentDistributorId || defaultOrderDistributorId(sorted);
        setCurrentId(current);
        setNextId(current);
      })
      .catch(() => toast.error('Failed to load distributors'));
  }, [docId, currentDistributorId]);

  if (status !== 'draft') return null;

  const current = distributors.find((d) => d.id === currentId);
  const next = distributors.find((d) => d.id === nextId);
  const leavingOwn =
    kind === 'lieferschein' || current?.is_self === true;
  const goingExternal = next ? next.is_self !== true : false;
  const unchanged = Boolean(nextId) && nextId === currentId;

  const apply = async () => {
    if (!nextId || unchanged) return;
    setSaving(true);
    try {
      const base = kind === 'lieferschein' ? '/lieferscheine' : '/bestellungen';
      const res = await api.patch(`${base}/${docId}/distributor`, {
        distributor_id: nextId,
      });
      const docs = res.data?.documents || [];
      toast.success(
        formatReassignToast(docs) || `Moved ${docNumber} to ${next?.name || 'distributor'}`
      );
      onMoved(docs);
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const handleClick = () => {
    if (leavingOwn && goingExternal) {
      setConfirmOpen(true);
      return;
    }
    void apply();
  };

  return (
    <div className="mb-4 space-y-2">
      <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">
        Distributor
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          value={nextId}
          onChange={(e) => setNextId(e.target.value)}
          className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm min-h-[48px]"
        >
          {distributors.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
              {d.is_self ? ' (Own Delivery)' : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={saving || !nextId || unchanged}
          onClick={handleClick}
          className="min-h-[48px] px-4 bg-gray-900 text-white rounded-xl text-sm font-bold disabled:opacity-40"
        >
          {saving ? 'Saving...' : 'Change'}
        </button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Remove delivery note?"
        message={`${docNumber} will be deleted from the Own delivery pick list because this order will go to ${next?.name || 'another distributor'}. The order lines stay.`}
        confirmText="Change distributor"
        variant="warning"
        onConfirm={() => void apply()}
      />
    </div>
  );
};
